// Copyright (c) 2025 DTCC and/or its affiliates. All rights reserved.
// SPDX-License-Identifier: Apache-2.0
import { useCallback, useMemo } from 'react';
import { Alert, Box, Typography } from '@mui/material';
import { ClientOnboarderService } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/ClientOnboarder';
import { RegistrarService } from '@daml.js/dtc-registry-app/lib/Dtc/Registry/App/V3/Service/Registrar';
import { ContractId } from '@daml/types';
import FormDialog from '../../../components/Dialog/FormDialog';
import Loading from '../../../components/Loading';
import { useLedgerExercise } from '../../../hooks/mutations/ledger/useLedgerExercise';
import useMutate from '../../../hooks/mutations/useMutate';
import useCredentials from '../../../hooks/queries/credential/useCredentials';
import useAllowLists from '../../../hooks/queries/registry/allowList/useAllowLists';
import { useOperatorDenyLists } from '../../../hooks/queries/registry/denyList/useDenyLists';
import { useParticipantServices } from '../../../hooks/queries/registry/onboarding/useParticipantServices';
import useRegistrarService from '../../../hooks/queries/registry/onboarding/useRegistrarService';
import useServiceUpgradeConsents from '../../../hooks/queries/registry/onboarding/useServiceUpgradeConsents';
import {
  ERR_OFFBOARD_CLIENT_ONBOARDER,
  SUC_OFFBOARD_CLIENT_ONBOARDER,
} from '../../../utils/strings';
import { isMemberOfDenyList } from '../denyList/utils';

type DialogProps = {
  open: boolean;
  onClose: () => void;
  clientOnboarderServiceContractId: string;
  clientOnboarder: string;
};

export const OffboardClientOnboarderDialog = ({
  open,
  onClose,
  clientOnboarderServiceContractId,
  clientOnboarder,
}: DialogProps) => {
  const mutate = useMutate();
  const offboardClientOnboarderMutation = useLedgerExercise(
    RegistrarService.RegistrarService_OffboardClientOnboarder,
  );
  const allowLists = useAllowLists();
  const denyLists = useOperatorDenyLists();
  const credentials = useCredentials();
  const participantServices = useParticipantServices();
  const registrarService = useRegistrarService();
  const serviceUpgradeConsents = useServiceUpgradeConsents();

  const clientOnboarderDenyList = useMemo(
    () => denyLists.data?.find((dl) => dl.payload.owner === clientOnboarder) ?? null,
    [denyLists.data, clientOnboarder],
  );

  const clientOnboarderParticipantServices = useMemo(
    () =>
      participantServices.data?.filter((ps) => ps.payload.clientOnboarder === clientOnboarder) ??
      [],
    [participantServices.data, clientOnboarder],
  );

  const clientOnboarderAllowLists = useMemo(
    () => allowLists.data?.filter((al) => al.payload.clientOnboarder === clientOnboarder) ?? [],
    [allowLists.data, clientOnboarder],
  );

  const denyListMemberCredentialCids = useMemo(() => {
    if (!credentials.data || !clientOnboarderDenyList) return [];
    return credentials.data
      .filter((cred) => isMemberOfDenyList(cred, clientOnboarderDenyList.payload.name))
      .map((cred) => cred.contractId);
  }, [credentials.data, clientOnboarderDenyList]);

  const clientOnboarderUpgradeConsentCids = useMemo(
    () =>
      serviceUpgradeConsents.data
        ?.filter((c) => c.payload.authorizer === clientOnboarder)
        .map((c) => c.contractId) ?? [],
    [serviceUpgradeConsents.data, clientOnboarder],
  );

  const hasParticipantServices = clientOnboarderParticipantServices.length > 0;
  const hasAllowLists = clientOnboarderAllowLists.length > 0;
  const hasBlockingContracts = hasParticipantServices || hasAllowLists;

  const offboardClientOnboarder = useCallback(async () => {
    if (!clientOnboarderDenyList || !registrarService.data) return;

    const payload = {
      cid: registrarService.data.contractId,
      argument: {
        clientOnboarderServiceCid:
          clientOnboarderServiceContractId as ContractId<ClientOnboarderService>,
        clientOnboarder,
        denyListCid: clientOnboarderDenyList.contractId,
        credentialCids: denyListMemberCredentialCids,
        existingServiceUpgradeConsents: clientOnboarderUpgradeConsentCids,
      },
    };

    await mutate(
      offboardClientOnboarderMutation,
      payload,
      SUC_OFFBOARD_CLIENT_ONBOARDER,
      ERR_OFFBOARD_CLIENT_ONBOARDER,
    );
    onClose();
  }, [
    clientOnboarder,
    clientOnboarderServiceContractId,
    clientOnboarderDenyList,
    clientOnboarderUpgradeConsentCids,
    denyListMemberCredentialCids,
    registrarService,
    onClose,
    mutate,
    offboardClientOnboarderMutation,
  ]);

  const isLoadingQueries =
    allowLists.isLoading ||
    credentials.isLoading ||
    denyLists.isLoading ||
    participantServices.isLoading ||
    registrarService.isLoading ||
    serviceUpgradeConsents.isLoading;
  const missingData = !clientOnboarderDenyList || !registrarService.data;
  const mutationIsPending = offboardClientOnboarderMutation.isPending;
  const disableSubmit =
    isLoadingQueries || missingData || hasBlockingContracts || mutationIsPending;

  return (
    <FormDialog
      open={open}
      title="Offboard Client Onboarder"
      onCancel={onClose}
      onSubmit={offboardClientOnboarder}
      submitName="Offboard"
      isSubmitDisabled={disableSubmit}
      submitButtonSxProps={{ color: 'error' }}
    >
      {isLoadingQueries ? (
        <Loading />
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {hasParticipantServices && (
            <Alert severity="error" sx={{ mb: 1 }}>
              Client onboarders with participants cannot be offboarded.
            </Alert>
          )}
          {hasAllowLists && (
            <Alert severity="error" sx={{ mb: 1 }}>
              Client onboarders with allow lists cannot be offboarded.
            </Alert>
          )}
          <Typography variant="body2" color="text.secondary">
            This will offboard the client onboarder and clean up all related contracts:
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {clientOnboarderDenyList ? '1' : '0'} deny list to archive.
          </Typography>
        </Box>
      )}
    </FormDialog>
  );
};

export default OffboardClientOnboarderDialog;
